/**
 * Who is looking at a module, who else has played it, and what that earns.
 *
 * A module is one course read by four different people, and the same card has to
 * answer each of them: a learner wants to know they are behind their classmates
 * and that there is a record to take; a teacher and a parent want the way in and
 * nothing else; the maker wants their own work. The card cannot ask the reader who
 * they are, so this is the one place that decides.
 *
 * ## The role, from the strongest evidence down
 *
 *  - **The maker.** No question to ask: it is their course.
 *  - **A seat in a classroom.** When a module is read in a room, the seat decides
 *    who somebody is — the app's own rule for every other classroom decision (see
 *    `#/state/classrooms/account`), and the only signal that is certain, because
 *    the room was joined on purpose.
 *  - **The account type.** `learner`, `parent`, `creator`, `organisation`, as
 *    onboarding records it. Certain when it is there, and *absent* on most
 *    deployments today: the AppView does not return the field yet, which is why
 *    nothing below is built on it alone.
 *  - **The level.** A viewer whose own school level is the module's level is a
 *    learner of that level — the most direct reading of *students in the same
 *    level*, and the one that survives a missing account type.
 *  - **A result of their own.** Somebody who has played a capsule of this module
 *    is a player, whatever their profile says. This is the signal that makes the
 *    feature appear for a learner whose type and level are both unknown, and it is
 *    the honest one: they have already done the work.
 *
 * The last two are *earned* rather than stated, and they need the class to be read
 * before they can be applied — so they live in `buildModuleAudience`, which has the
 * board, and not in `moduleViewerRole`, which is the ladder of things a viewer says
 * about themselves.
 *
 * Anything left over is `unknown`, and an unknown viewer is *not* a peer. The
 * failure that matters is telling a teacher to *challenge* their own class, so the
 * ladder defaults to the quiet answer — the way in — and the peer experience is
 * reached by evidence rather than by assumption.
 *
 * ## The peers
 *
 * The board already holds everybody who played, ranked (see
 * `#/lib/moduleLeaderboard`), so the peers are read from it rather than counted a
 * second time: board order, the viewer's own row left out. A peer whose *level* a
 * play reported must match the module's; a peer whose level nobody reported is
 * kept, because an absent field is not a different year, and dropping rows on the
 * strength of a field the server does not send would empty the strip on every
 * deployment that has not implemented it.
 *
 * ## The top three are the board's, not this block's
 *
 * Nothing here draws a podium any more. The class's own faces are the way to the
 * results (`#/components/modules/ModuleAudience`), and the board's screen does the
 * podium properly (`#/components/modules/ModuleLeaderboardCard`); a second, smaller
 * copy of the same three names on the card was saying the same thing twice in a
 * place with no room for it. What is left here is the *count* of finishers, which
 * the card reads to say how far the class is from unlocking the top three — and
 * the board's own gate is still the only thing that decides when they exist
 * (`mode === 'leaderboard'`, see `#/lib/moduleLeaderboard`).
 *
 * ## The names are the class's, and every reader gets them
 *
 * `leaders`, `prizeWinners` and `friend` are *not* gated on the reader competing,
 * and that is the change from the first build of this file. A teacher is the one
 * reader with a stake in a class they are not racing: the faces at the top of the
 * board are the evidence that the work is being done, and a name they follow among
 * them is the loudest version of the same fact — so the block answers *who played*
 * for the teacher exactly as it does for the learner, and only the *tone* differs
 * (the challenge punch belongs to a competitor; see `ModuleAudience`). Nothing is
 * leaked by it: every name here is already on the board the teacher can open, and
 * `peers` — the reader's own year, the one list that is about *them* — is still a
 * learner's alone.
 *
 * ## The names, and the two gates that earn them
 *
 * The block under the playlist says *how many played* until there are enough
 * players for that sentence to be dull, and then it says *who they are*. Two
 * separate thresholds, because they are two separate questions:
 *
 *  - **`MODULE_LEADERBOARD_LEADERS_PLAYS`** (a *play* count) earns `leaders` — the
 *    three in front, read off the top of the board's own order, while the podium
 *    itself may still be locked. A crowd of ninety-five belongs to somebody, and
 *    hiding it to protect a trophy nobody has won yet is the wrong trade.
 *  - **`MODULE_LEADERBOARD_MIN_FINISHERS`** (a *finisher* count, the board's own
 *    gate) earns `prizeWinners` — the leading ten *finishers*, which is the set the
 *    card calls *ten prize winners*.
 *
 * Both come out of the board's single ordering, so a count and the names printed
 * beside it can never disagree about who is ahead.
 *
 * ## A teacher's feed is not a classroom
 *
 * A module is written by one teacher and read by others, and the reader who is
 * another teacher has *nothing to compete for* — which the button said by saying
 * *play module*, the same words it gives a parent and a stranger, while what the
 * reader is actually being offered is somebody else's version of a lesson they
 * teach. So a module that shares the reader's **subject** or their **year** is
 * offered as *play for inspiration* instead (`ModuleViewerWork`, and
 * `ModuleAudienceCta`): the two things that make a course *theirs* are the two
 * things that make somebody else's course worth opening.
 *
 * It is a claim about the reader and never about the course, so it needs evidence
 * from the reader's side — the levels and subjects of the work they have published
 * — and a reader whose work nobody knows simply gets the plain way in. The maker is
 * not a teacher here: their own module is not inspiration, and it keeps *play
 * module*.
 *
 * ## A friend outranks a stranger
 *
 * `friend` is the best-ranked player this viewer *follows*, and it is the one
 * field here that the board cannot supply: follow state lives on a profile, not on
 * a play (`ProfileView.viewer.following`), so it is handed in as a set of dids by
 * the caller that looked the leaders up (`#/components/modules/useModuleFollowing`).
 * Absent — a signed-out reader, a failed request, a card that never asked — is
 * *nobody is a friend*, which is the only safe reading of a missing answer: the
 * alternative names a stranger as someone the reader knows, which is the one
 * mistake this block would not survive.
 *
 * ## The way to the board is the class itself
 *
 * `playerFaces` is the one list here that is *not* filtered by the reader: the
 * board's leading rows, in rank order, as they stand. The peers above are one
 * learner's own year and are empty for everybody else, which is right for a
 * sentence about *your classmates* and wrong for a strip about *who played this* —
 * a teacher whose class has played wants those faces on it as much as the learners
 * do, and an audience that drew them nothing would have hidden the very thing the
 * teacher came for. It is still the same board, read once; and it is now the strip
 * itself for a reader the peers are empty for, rather than a door beside it (see
 * `moduleAudienceFaces`).
 */
import {type MacroMessageDescriptor, msg} from '@lingui/core/macro'

import {gradeLabel, levelAgreesWithGrade} from '#/lib/curriculum'
import {
  type ModuleLeaderboard,
  type ModulePlayerScore,
} from '#/lib/moduleLeaderboard'
import {
  MODULE_LEADERBOARD_LEADERS_PLAYS,
  MODULE_LEADERBOARD_MIN_FINISHERS,
} from '#/lib/modulePlaylist'
import {moduleRunIsClose, type ModuleRunProgress} from '#/lib/moduleRun'

/*
 * How many faces the strip draws before it becomes a number.
 *
 * Four, and the number carries the rest. The strip sits beside a sentence on a
 * card a third narrower than the module's own page, and a fifth face pushed the
 * count bubble onto the next line — where it read as a separate fact rather than
 * as *and the rest of them*. The count is what says how many there are; the faces
 * are only there to say *people*, so the strip is as long as it can be while the
 * bubble stays beside it.
 */
export const MODULE_PEER_FACES = 4

/** How many peers the sentence names before it becomes *and N others*. */
export const MODULE_PEERS_NAMED = 3

/**
 * How many of the leaders the sentence names once the class is a crowd.
 *
 * Three, because a podium is three and because a sentence that names five people
 * is a list; the fourth place is what the board is for (see
 * `#/screens/ModuleLeaderboard`).
 */
export const MODULE_LEADERS_NAMED = 3

/**
 * How many finishers the card calls *prize winners* when it counts them out.
 *
 * Ten, because that is the size the sentence was asked for (*Ten prize winners*)
 * — a number a reader can picture — and because it is the widest set of rows the
 * card is ever willing to look up follow state for.
 */
export const MODULE_PRIZE_WINNERS = 10

/** A seat in a classroom, as `#/state/classrooms` names them. */
export type ModuleViewerSeat = 'student' | 'parent' | 'teacher'

/** The account kinds onboarding records, as `#/state/classrooms/account` reads them. */
export type ModuleAccountType =
  | 'learner'
  | 'parent'
  | 'creator'
  | 'organisation'

/** What the reader's own side of the module is. */
export type ModuleViewerRole =
  | 'author'
  | 'teacher'
  | 'parent'
  | 'learner'
  | 'unknown'

/** What the card's one button should say, before it is said. */
export type ModuleAudienceCta =
  /** The way in, for everyone who is not competing. */
  | 'play'
  /**
   * A learner whose board is empty. The card's own line for them is the one piece
   * of copy on it that is encouragement rather than a reading — there is nothing
   * yet to read — see `ModuleAudience`.
   */
  | 'first'
  /** A learner with classmates ahead of them. */
  | 'challenge'
  /** A learner who has played and can take the record. */
  | 'challenge-record'
  /**
   * The learner who *holds* the record, with a class behind them.
   *
   * Its own word because the other one was wrong for them: *challenge and break
   * the record* is an invitation to take a record, and the reader this fires for is
   * the reader who already has it — the card would be telling them to beat
   * themselves. What is true of a first place is that it can be lost, which is what
   * this says instead. Read off the board's own rank, not guessed at.
   */
  | 'defend-record'
  /**
   * A reader who does not compete, reading a module that is in their own line of
   * work: *play for inspiration* rather than *play module*.
   *
   * See the header for the rule, and `moduleIsTheReadersWork` for the two
   * comparisons it is read from. Nothing about it is competitive: the module is
   * somebody else's version of the reader's own subject or year, and what they are
   * offered is the way in to what is inside it.
   */
  | 'inspiration'

/**
 * The word on the block's one button, chosen and not yet translated.
 *
 * Three things decide it, in this order, and the order is the whole rule:
 *
 *  - **A finished run starts again.** The reader has already done the thing the
 *    challenge was for, so the honest word for their next press is the one that
 *    names the press: *play again*. A record they hold is defended by playing, not
 *    by a word written for somebody who has not played yet.
 *  - **A run under way is continued**, and shouts when it is close
 *    (`moduleRunIsClose` — three capsules in, or halfway). That close, the only
 *    honest word left is *finish it*, and the fire is the card's one raised voice.
 *  - **Otherwise the reader's own situation talks** — the word `cta` already
 *    decided above, said in as few words as each case allows. A button is a promise,
 *    and a promise is shorter than a description: *break the record*, not
 *    *challenge and break the record*. The one that is longer than the way in is
 *    the teacher's (*play for inspiration*), and it earns the extra word because it
 *    is the only button on the card that says *what a course is for* rather than
 *    what pressing it gets you.
 *
 * Returned as a descriptor rather than as a string so the caller translates it with
 * the same `l` as everything around it, and kept here rather than in the component
 * so the *choice* can be read — and asserted — without a React tree.
 */
export function modulePlayWord({
  cta,
  progress,
}: {
  cta: ModuleAudienceCta
  /** How far this reader's run has got, when anything knows. */
  progress?: ModuleRunProgress | null
}): MacroMessageDescriptor {
  if (progress?.finished) return msg`Play again`
  if (progress && progress.done > 0) {
    return moduleRunIsClose(progress) ? msg`🔥 Finish it!` : msg`Continue`
  }
  switch (cta) {
    case 'challenge':
      return msg`Challenge`
    case 'defend-record':
      return msg`Defend your record`
    case 'challenge-record':
      return msg`Break the record`
    case 'first':
      return msg`Be the first player`
    case 'inspiration':
      return msg`Play for inspiration`
    default:
      return msg`Play module`
  }
}

/**
 * What the reader works in, read off the modules they have published.
 *
 * Two lists and no more, because these are the two comparisons the one rule that
 * needs them makes — the module's subject, and its year (`moduleIsTheReadersWork`).
 * A level here is not the reader's own school year (`ModuleViewer.level`, which is
 * what a *learner* is in): this is the year they *teach*, which is the year of the
 * courses they wrote. Absent altogether means *nobody has read their work*, which
 * is not the same statement as *they have published nothing* — both make no match,
 * and only one of them is something the app could claim.
 */
export type ModuleViewerWork = {
  /** Level codes (`Level_A5`) of the modules this reader has published. */
  levels: readonly string[]
  /** Subject codes (`Maths`) of the modules this reader has published. */
  subjects: readonly string[]
}

/** The reader, as much of them as anything knows. */
export type ModuleViewer = {
  did?: string
  /** The module was made by this account. */
  isAuthor?: boolean
  accountType?: ModuleAccountType | null
  /** Their seat in the classroom this module is being read in, if any. */
  seat?: ModuleViewerSeat | null
  /** The level they play at, when their profile says. */
  level?: string | null
  /**
   * The subjects and levels this reader works in, when anything knows them.
   *
   * Read only for the one comparison that needs the reader's own side of the
   * course — *is this module in their line of work?* (`moduleIsTheReadersWork`) —
   * and absent for everybody the app has never seen publish anything.
   */
  work?: ModuleViewerWork | null
  /**
   * A forced role, for the dev preview only.
   *
   * The peer experience is for a reader the app has evidence about, and evidence
   * is exactly what a preview browser with one signed-in teacher does not have —
   * so the preview names the role out loud instead. Nothing outside `IS_DEV`
   * passes this (see `#/lib/routes/types`).
   */
  as?: ModuleViewerSeat | 'learner' | null
}

export type ModuleAudience = {
  role: ModuleViewerRole
  /** Whether the reader competes: the strip, the sentence and the challenge. */
  isPeer: boolean
  /** Everybody else who played, in board order, the viewer left out. */
  peers: ModulePlayerScore[]
  /**
   * The board's leading rows, in rank order, whoever is reading.
   *
   * What a reader with no year of their own is shown — a teacher's strip is the
   * class itself, the faces they would otherwise never see. Unfiltered by the
   * reader's role on purpose (see the header), and capped at `limit` like the
   * peers. */
  playerFaces: ModulePlayerScore[]
  /** The first few of them, for the faces and the sentence. */
  named: ModulePlayerScore[]
  /** How many beyond `named` — the *and N others* of the sentence. */
  others: number
  /** Distinct learners who played, finished or not. */
  played: number
  /** Learners who played every capsule. */
  finishers: number
  /**
   * The three in the lead, once there are enough players to be worth naming.
   *
   * The lower, *played*-counted gate (`MODULE_LEADERBOARD_LEADERS_PLAYS`) rather
   * than the board's finisher threshold, so the card can say who is out in front
   * while the podium itself is still locked. Empty below the gate.
   */
  leaders: ModulePlayerScore[]
  /**
   * The leading ten finishers, once a hundred have finished.
   *
   * The same gate as the podium — and the *same comparison*: the board cuts to a
   * podium at a hundred finishers (`mode` in `#/lib/moduleLeaderboard` compares
   * with `>=`), so this is `>=` too. A card that waited for one more finisher was
   * hiding the winners the results screen was already showing, and it left the
   * countdown sentence (*one more finisher and the top three appear*) pointing at a
   * podium that had already appeared. Read ten rows deep: the names a card counts
   * out when it says *ten prize winners*. Empty below the gate.
   */
  prizeWinners: ModulePlayerScore[]
  /**
   * The best-ranked player the viewer follows, when one of them played.
   *
   * `null` both when nobody they follow played *and* when the app has no follow
   * state to read — an absent answer is not a friendship, and a card that guessed
   * at one would name a stranger as a friend.
   */
  friend: ModulePlayerScore | null
  /** The viewer's own row, when they have one. */
  viewer: ModulePlayerScore | null
  cta: ModuleAudienceCta
}

/**
 * The part of an audience that *copy* is read from.
 *
 * The block under a module's playlist is wording — the sentence about the class and
 * the punch under it, the countdown to the top three, the line about the run, the
 * reader's own row, and the word on the button — and the wording is decided by
 * these facts and by nothing else. Narrow on purpose: the dev spec sheet has to
 * invent a reader and a board for every case of the reel
 * (`#/components/modules/ModuleAudience`), and a case that can state only these
 * facts cannot claim one the copy does not read — nor can the copy reach for one
 * the case has not invented. `role`, `peers` and the strip's own rows are left out
 * because they decide *who is drawn*, not *what is said*.
 */
export type ModuleCaseFacts = Pick<
  ModuleAudience,
  | 'isPeer'
  | 'played'
  | 'finishers'
  | 'leaders'
  | 'prizeWinners'
  | 'friend'
  | 'viewer'
  | 'cta'
>

/**
 * The reader's role, from what the reader says about themselves.
 *
 * The two *earned* rungs of the ladder — a level that is the module's, a result on
 * this module's board — need the class and are applied by `buildModuleAudience`;
 * what is here is the part that is true before anything is fetched. Exported
 * because a caller sometimes needs the role alone, and because a rule this
 * consequential should be one function rather than a paragraph of `&&` at three
 * call sites.
 */
export function moduleViewerRole(viewer: ModuleViewer): ModuleViewerRole {
  if (viewer.isAuthor) return 'author'

  const forced = viewer.as
  if (forced === 'learner') return 'learner'
  if (forced === 'student') return 'learner'
  if (forced === 'parent') return 'parent'
  if (forced === 'teacher') return 'teacher'

  if (viewer.seat === 'student') return 'learner'
  if (viewer.seat === 'parent') return 'parent'
  if (viewer.seat === 'teacher') return 'teacher'

  switch (viewer.accountType) {
    case 'learner':
      return 'learner'
    case 'parent':
      return 'parent'
    case 'creator':
    case 'organisation':
      return 'teacher'
    default:
      break
  }

  return 'unknown'
}

/** Whether a level nobody reported should be treated as a mismatch. Never. */
function levelAgrees(
  peerLevel: string | undefined,
  moduleLevel: string,
): boolean {
  if (!peerLevel) return true
  return peerLevel === moduleLevel
}

/**
 * Whether a level of the reader's own names the same year as the module's.
 *
 * `levelAgreesWithGrade` is this app's translation between the two ways a year is
 * written — the record's code (`Level_A1`) and the words a person picked on the way
 * in (`1st year`) — and it is deliberately *lenient*: an absent or unnameable side
 * is agreement, because it is used to promote an otherwise unknown reader up the
 * ladder and refusing on a field the server does not send would hide the feature.
 * The question here is the narrower one — whether the two levels *are* the same
 * year — so everything it cannot show is a no: two codes that are not the same
 * string are only the same year when the vocabulary can name the module's, and a
 * level nobody can name is nobody's.
 */
function levelIsTheReadersWork(moduleLevel: string, own: string): boolean {
  if (!own) return false
  if (moduleLevel === own) return true
  if (!gradeLabel(moduleLevel)) return false
  return levelAgreesWithGrade(moduleLevel, own)
}

/**
 * Whether a module is in the reader's own line of work.
 *
 * The rule behind the teacher's button (`ModuleAudienceCta`): a module that shares
 * the **subject** or the **year** of the work this reader has published is somebody
 * else's version of a course they teach too, and what it offers them is what is
 * inside it rather than a record to take. Either comparison fires, because either
 * one alone makes the course worth opening.
 *
 * Nothing is fired on an absent side, and that is the whole of the caution here. A
 * reader whose work nobody has read, a module whose record names no subject, a
 * level the vocabulary cannot translate — each leaves the button at the plain way
 * in, which is the honest word whenever nothing connects the two of them. The
 * subject is compared as the record wrote it (`Maths`) and not as a label, because
 * both sides of *that* comparison are the same vocabulary's codes.
 */
export function moduleIsTheReadersWork(
  module: {level?: string | null; subject?: string | null},
  work: ModuleViewerWork | null | undefined,
): boolean {
  if (!work) return false

  const subject = module.subject?.trim()
  if (subject && work.subjects.includes(subject)) return true

  const level = module.level?.trim()
  if (!level) return false
  return work.levels.some(own => levelIsTheReadersWork(level, own.trim()))
}

/**
 * The module, from the reader's side.
 *
 * `board` is the whole class's result (see `#/lib/moduleLeaderboard`) and `level`
 * the module's own school level; both are optional, because the card is drawn
 * before either has arrived and an audience nobody has heard about yet is simply
 * nobody.
 */
export function buildModuleAudience({
  board,
  viewer,
  level,
  subject,
  following,
  limit = MODULE_PEER_FACES,
}: {
  board: ModuleLeaderboard | undefined
  viewer: ModuleViewer
  /** The module's level code (`Level_A5`), when the record states one. */
  level?: string | null
  /**
   * The module's subject code (`Maths`), when the record states one.
   *
   * Read for one thing only — whether the course is in the reader's own line of
   * work (`moduleIsTheReadersWork`) — and never for the class: a subject is not a
   * year, and nothing about the peers or the board changes with it.
   */
  subject?: string | null
  /**
   * The dids this viewer follows, when the app knows them.
   *
   * Optional, and the only input here that the board cannot supply: follow state
   * lives on a profile (`ProfileView.viewer.following`), not on a play, so the
   * caller that has looked the leaders up hands the answer in (see
   * `#/components/modules/useModuleFollowing`). Omitted — a signed-out reader, a
   * failed request, a card that never asked — means *nobody is a friend*, never
   * *everybody is*.
   */
  following?: ReadonlySet<string>
  /** How many faces a row of them draws — the audience strip, and the door. */
  limit?: number
}): ModuleAudience {
  const did = viewer.did
  const moduleLevel = typeof level === 'string' && level ? level : null
  const moduleSubject = typeof subject === 'string' && subject ? subject : null

  const all = board?.all ?? []
  const viewerRow = did ? (all.find(row => row.did === did) ?? null) : null

  /*
   * What the reader has *done* outranks what their profile does not say.
   *
   * Only an otherwise-unknown reader is upgraded: a teacher whose account type is
   * known is a teacher even after playing their own class's course, and a maker is
   * a maker.
   */
  const stated = moduleViewerRole(viewer)
  const viewerLevel =
    typeof viewer.level === 'string' && viewer.level ? viewer.level : null
  /*
   * The viewer's own grade counts as *this level* when it names the same year.
   *
   * It is usually not the same string: a profile carries what the person picked
   * on the way in (`1st year`, from their country's own curriculum) while the
   * module carries the record's code (`Level_A1`). `levelAgreesWithGrade` is the
   * translation, and either side saying nothing leaves this to the other signals
   * on the ladder rather than deciding.
   */
  const earnedTheirPlace =
    !!viewerRow ||
    (!!viewerLevel && levelAgreesWithGrade(moduleLevel, viewerLevel))
  const role: ModuleViewerRole =
    stated === 'unknown' && earnedTheirPlace ? 'learner' : stated
  const isPeer = role === 'learner'

  /*
   * A learner is only ever compared with their own year.
   *
   * The board keeps a learner's level when a play reported one, and a row from a
   * *different* level is dropped here rather than in the board: the class's full
   * results are still what a teacher reads, and it is only the peer strip — this
   * one learner's classmates — that is filtered.
   */
  const peers = isPeer
    ? all.filter(
        row =>
          row.did !== did &&
          (!moduleLevel || levelAgrees(row.level, moduleLevel)),
      )
    : []

  const played = all.length
  const finishers = board?.finishers ?? 0
  const ranked = board?.ranked ?? []

  /*
   * The two crowds worth naming, and neither is asked for below its gate.
   *
   * The leaders are the top of `all` rather than of `ranked`, because the gate
   * that earns them counts *players*: a module somebody has played has somebody
   * ahead, whether or not a hundred people have finished it. `all` is already in
   * board order (`#/lib/moduleLeaderboard`), so its first three rows are the three
   * in the lead — finishers first, and a player who has not finished only when
   * nobody ahead of them has.
   *
   * Both are read for every reader, not only for a learner (see the header): a
   * teacher reading their own class is the reader who most wants to know that
   * somebody has finished. The gates stay where they are, because they are about
   * the *board* — how much of a class is behind the number — and not about who is
   * looking at it.
   */
  const leaders =
    played > MODULE_LEADERBOARD_LEADERS_PLAYS
      ? all.slice(0, MODULE_LEADERS_NAMED)
      : []
  const prizeWinners =
    finishers >= MODULE_LEADERBOARD_MIN_FINISHERS
      ? ranked.slice(0, MODULE_PRIZE_WINNERS)
      : []

  /*
   * The one name the sentence is allowed to lead with, and the one face the strip
   * puts in front of the class.
   *
   * The board is already in rank order (`#/lib/moduleLeaderboard`), so the *first*
   * match is the best-placed friend — 1st place if they are 1st, which is the
   * whole point of leading with them. Searched over the whole class rather than
   * over the peers, because a friend in another year is still a friend, and a
   * friend who stopped after two capsules is still someone whose name the reader
   * recognises. Read for every reader along with the two crowds above: a teacher
   * whose student is on the board is exactly the reader for whom a familiar name
   * means the most, and the follow state is the reader's own either way.
   */
  const friend = following?.size
    ? (all.find(row => following.has(row.did)) ?? null)
    : null

  const hasPlayed = !!viewerRow

  return {
    role,
    isPeer,
    peers,
    playerFaces: all.slice(0, Math.max(0, limit)),
    named: peers.slice(0, MODULE_PEERS_NAMED),
    others: Math.max(0, peers.length - MODULE_PEERS_NAMED),
    played,
    finishers,
    leaders,
    prizeWinners,
    friend,
    viewer: viewerRow,
    /*
     * The one button, in the reader's own situation.
     *
     * A learner with an *empty board* is *first* — asking them to *be the first
     * player* of a module somebody has already played is the one thing this word
     * must never say. It reads `played` and not `peers` for exactly that reason:
     * the peers are the reader's own year, and a board whose rows are all another
     * year's is still a board somebody is on.
     *
     * A learner holding the top place is then asked to keep it, not to take it
     * (`defend-record`); anybody else who has played is challenging a record that is
     * not theirs yet, and anybody else who has not is challenging the class.
     *
     * A reader who does not compete gets the way in — and the one of them the way in
     * is wrong for is the reader this module is *their kind of course*: another
     * teacher of the same subject or year is offered what is inside it
     * (`inspiration`), which is the same press under a word that says what for. The
     * maker is not that reader: their own module is not inspiration, and they keep
     * the plain word every non-competitor gets.
     */
    cta: !isPeer
      ? moduleIsTheReadersWork(
          {level: moduleLevel, subject: moduleSubject},
          viewer.work,
        ) && !viewer.isAuthor
        ? 'inspiration'
        : 'play'
      : played === 0
        ? 'first'
        : viewerRow?.rank === 1
          ? 'defend-record'
          : hasPlayed
            ? 'challenge-record'
            : 'challenge',
  }
}

/**
 * The faces to draw, best first: the reader's own result, the one they follow,
 * then the class.
 *
 * Three things decide this, in order of how much they are about the reader:
 *
 *  - **A learner who has played is in the row.** They belong among the faces of
 *    the people they are up against, and leaving themselves out would be the one
 *    absence they would notice. They go first, so the strip opens on them.
 *  - **The one they follow goes next.** A name the reader chose is worth more than
 *    any rank, which is the rule the sentence already follows — and a *face* is the
 *    version of that rule a reader recognises without reading: someone they follow
 *    can be sixtieth, and the strip still opens on the person they know.
 *  - **Then the class**, which is the reader's own year for a learner and the
 *    board's own leaders for everybody else (a teacher's strip is the class's top
 *    players — see `playerFaces`).
 *
 * Deduplicated, because the friend is usually already in the class and a face
 * drawn twice would be two people who are one, and capped at `limit` — the length
 * the `+N` bubble beside it is cut for.
 */
export function moduleAudienceFaces(
  audience: ModuleAudience,
  limit: number = MODULE_PEER_FACES,
): ModulePlayerScore[] {
  const pool = audience.isPeer ? audience.peers : audience.playerFaces
  const own = audience.isPeer && audience.viewer ? [audience.viewer] : []
  const followed = audience.friend ? [audience.friend] : []

  const seen = new Set<string>()
  const strip: ModulePlayerScore[] = []
  for (const row of [...own, ...followed, ...pool]) {
    if (seen.has(row.did)) continue
    seen.add(row.did)
    strip.push(row)
    if (strip.length === limit) break
  }
  return strip
}
